import { LiveDataDetailImageQueryGuarded } from '../DetailView/DetailImageQuery';
import { PredictionColumnsLayout } from '../Prediction/PredictionColumnsLayout';
import { LiveDataAttributesColumnQueryGuarded } from '../Prediction/AttributesColumn/AttributesColumn';
import { LiveDataMaterialColumnQueryGuarded } from '../Prediction/MaterialColumn/MaterialColumn';
import { shouldDisplayEdgeEstimation, shouldDisplaySoilClassification } from '../Prediction/utils';
import { useMemo } from 'react';
import { useResolvedBillableMaterials } from '../BillableMaterials/useResolvedBillableMaterials';
import BillableMaterialColumn from '../BillableMaterials/predictionColumn/BillableMaterialColumn';
import { useTruckRecordingMipImage } from '../../api/queries/truckRecordingMipImage';
import { useTruckRecordingMipMaterialClassification } from '../../api/queries/truckRecordingMipMaterialClassification';
import { useTruckRecordingMipSoilClassification } from '../../api/queries/truckRecordingMipSoilClassification';
import { useTruckRecordingMipEdgeEstimation } from '../../api/queries/truckRecordingMipEdgeEstimation';
import { useTruckRecordingMipVolumeEstimation } from '../../api/queries/truckRecordingMipVolumeEstimation';
import { MipActivityRecording } from './hooks/demoLiveActivities';
import { ActivityTileLayout } from './ActivityTileLayout';
import { useEdgeSizeBoundingBoxes } from '../../utils/useEdgeSizeBoundingBoxes';

interface MipActivityTileProps {
  organisationId: string;
  jobsiteId: string;
  recording: MipActivityRecording;
  isFocus: boolean;
}

export default function MipActivityTile({ organisationId, jobsiteId, recording, isFocus }: MipActivityTileProps) {
  const displayedCaptureStartAt = recording.displayedCaptureStartAt;

  const image = useTruckRecordingMipImage({
    jobsiteId,
    mipReportId: recording.imageStatus === 'finished' ? recording.mipReportId : undefined,
  });

  // directly from the recording, maybe change once lps in mip
  const licensePlate = recording.licensePlate || '-';

  const materialClassification = useTruckRecordingMipMaterialClassification({
    jobsiteId,
    mipReportId: recording.materialStatus === 'finished' ? recording.mipReportId : undefined,
    enabled: recording.materialStatus === 'finished',
  });

  const displaySoilClassification = useMemo(
    () => shouldDisplaySoilClassification(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const soilClassification = useTruckRecordingMipSoilClassification({
    jobsiteId,
    mipReportId: recording.soilStatus === 'finished' ? recording.mipReportId : undefined,
    enabled: recording.soilStatus === 'finished' && displaySoilClassification,
  });

  const displayEdgeEstimation = useMemo(
    () => shouldDisplayEdgeEstimation(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const edgeEstimation = useTruckRecordingMipEdgeEstimation({
    jobsiteId,
    mipReportId: recording.mipReportId,
    enabled: recording.edgeStatus === 'finished' && displayEdgeEstimation,
  });

  const volumeEstimation = useTruckRecordingMipVolumeEstimation({
    jobsiteId,
    mipReportId: recording.volumeStatus === 'finished' ? recording.mipReportId : undefined,
    enabled: recording.volumeStatus === 'finished',
  });
  const { resolvedMaterials } = useResolvedBillableMaterials(
    organisationId,
    jobsiteId,
    materialClassification.data,
    soilClassification.data,
    edgeEstimation.data
  );

  const { selectedAttribute, edgeSizesBoundingBoxes, handleAttributeSelect } = useEdgeSizeBoundingBoxes(
    edgeEstimation.data
  );

  return (
    <ActivityTileLayout captureStartAt={displayedCaptureStartAt} licensePlate={licensePlate} isFocus={isFocus}>
      <LiveDataDetailImageQueryGuarded
        title={'delivery.loading_area'}
        imageQuery={image}
        downloadFileName={`${displayedCaptureStartAt}.jpg`}
        imageStatus={recording.imageStatus}
        boundingBoxes={edgeSizesBoundingBoxes}
      />
      <PredictionColumnsLayout
        materialTypeColumn={
          <LiveDataMaterialColumnQueryGuarded
            materialClassificationQuery={materialClassification}
            resourceStatus={recording.materialStatus}
          />
        }
        attributesColumn={
          <LiveDataAttributesColumnQueryGuarded
            attributes={[
              { resourceStatus: recording.edgeStatus, query: edgeEstimation },
              { resourceStatus: recording.soilStatus, query: soilClassification },
              { resourceStatus: recording.volumeStatus, query: volumeEstimation },
            ]}
            isMaterialClassificationPending={materialClassification.isPending}
            selectedAttribute={selectedAttribute}
            onAttributeSelect={handleAttributeSelect}
          />
        }
        billableMaterialColumn={<BillableMaterialColumn isUserAdmin={false} materials={resolvedMaterials} />}
      />
    </ActivityTileLayout>
  );
}
